import { useEffect, useCallback, Fragment } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Paragraph from "@vfuk/core-paragraph/";
import Heading from "@vfuk/core-heading/";
import Spacing from "@vfuk/core-spacing/";
import manageAccessBanner from "@img/manageAccessBanner.png";
import Button from "@vfuk/core-button";
import { useNavigate } from "react-router-dom";
import { useUserManagementStore } from "src/hooks/stores";
import { SectionBanner, SpinnerLoader, Stack } from "@ucchub/ui";
import DashShell from "@components/common/DashShell";
import LinkWithIcon from "@vfuk/core-link-with-icon";
import { Crumbs } from "@ucchub/ui";
import { User } from "src/interfaces/user";
import breadCrumbs from "@json/common/crumbs.json";
import useCurrentUser from "@hooks/useCurrentUser";
import Icon from "@vfuk/core-icon";
import styled from "styled-components";
import Container from "@vfuk/core-container";
import ViewUserDetails from "./ViewUserDetails";
import ViewUserButtons from "./ViewUserButtons";
import { UserStatus } from "../UserManagement.types";

export const StyledSpan = styled.span`
  font-size: smaller;
  padding-left: 3px;
`;

const ViewUser = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "shared:users",
  });
  const navigate = useNavigate();

  const {
    isCurrentOrganisationLoading,
    isLoading: isCurrentUserLoading,
    currentUser,
    getCurrentOrganisation,
    isOrganisationStatus,
    vFAdmin,
  } = useCurrentUser();
  const { userId } = useParams() as { userId: string };

  const {
    loading: userLoading,
    error: userFetchError,
    actions: userActions,
    call: fetchUser,
  } = useUserManagementStore((state) => state.fetchUser);

  const isLoading =
    isCurrentUserLoading || userLoading || isCurrentOrganisationLoading;

  const user: User = useUserManagementStore((state) => state.user);

  const isActiveUser = user.status === "active";

  const loadUser = useCallback(() => {
    fetchUser(userId);
  }, [userId, fetchUser]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (user.organisation?.id && !isCurrentUserLoading) {
      if (vFAdmin && userActions.delete) {
        getCurrentOrganisation(user.organisation?.id);
      }
      if (!vFAdmin && userActions.delete) {
        if (currentUser?.orgId === user.organisation?.id) {
          getCurrentOrganisation(user.organisation?.id);
        }
      }
    }
  }, [vFAdmin, user, currentUser, isCurrentUserLoading]);

  const clearAllSelectedUsers = useUserManagementStore(
    (state) => state.clearAllSelectedUsers
  );

  const isUserDeactivate =
    !isLoading && currentUser?.userId === userId && isActiveUser;

  const notOrganisationStatus = !isLoading && !isOrganisationStatus;

  return (
    <DashShell>
      <Crumbs items={breadCrumbs.view_Users} />
      <SectionBanner
        heading={isLoading ? t("loading") : user.email}
        subtitle=""
        image={manageAccessBanner}
        iconName="users"
      />
      <Spacing
        spacingLevel={{
          top: 4,
          bottom: 4,
        }}
      >
        <LinkWithIcon
          appearance="secondary"
          text={t("users_back")}
          icon={{
            name: "chevron-left",
            justify: "left",
          }}
          onClick={() => {
            clearAllSelectedUsers();
            navigate("/users");
          }}
        />
      </Spacing>
      {isLoading && (
        <SpinnerLoader
          srText={t("loading_user_details") || "Loading user details"}
          text={t("loading_user_details") || "Loading user details"}
        />
      )}
      {!isLoading &&
        ((userFetchError !== undefined && userFetchError !== "") ||
          user.status === UserStatus.DELETED) && (
          <>
            <Paragraph justify="center">
              <span className="colorVFRed">
                {user.status === UserStatus.DELETED
                  ? t("user_deleted_message")
                  : userFetchError}
              </span>
            </Paragraph>
            <Spacing
              spacingLevel={{
                top: 3,
                bottom: 3,
              }}
            >
              <Button
                width="full"
                text={t("back")}
                appearance="alt1"
                onClick={() => {
                  clearAllSelectedUsers();
                  navigate("/users");
                }}
              />
            </Spacing>
          </>
        )}
      {!isLoading && userFetchError === "" && (
        <Spacing
          spacingLevel={{
            top: 4,
            bottom: 4,
          }}
        >
          {user.status !== UserStatus.DELETED && (
            <>
              <Container appearance="primary" elevation>
                <Stack justifyContent="space-between" align="baseline">
                  <Heading level={4} weight={3}>
                    {t("heading")}
                  </Heading>
                </Stack>
                <ViewUserDetails user={user} />
                {notOrganisationStatus && !isActiveUser && (
                  <Spacing spacingLevel={{ top: 2, bottom: 2 }}>
                    <Icon name="warn" group="state" size={2} />
                    <StyledSpan>
                      {t("view_user:reactivate_user_organisation")}
                    </StyledSpan>
                  </Spacing>
                )}
                {isUserDeactivate && isOrganisationStatus && (
                  <Spacing spacingLevel={{ top: 2 }}>
                    <Icon name="warn" group="state" size={2} />
                    <StyledSpan>
                      {currentUser.userType === "vodafone"
                        ? t("view_user:deactivate_own_vodafone_user_message")
                        : t("view_user:deactivate_own_customer_user_message")}
                    </StyledSpan>
                  </Spacing>
                )}
              </Container>
              <ViewUserButtons
                isLoading={isLoading}
                user={user}
                currentUserId={currentUser?.userId || ""}
                userId={userId}
                isActiveUser={isActiveUser}
                userActions={userActions}
                isUserDeactivate={isUserDeactivate}
                notOrganisationStatus={notOrganisationStatus}
              />
            </>
          )}
        </Spacing>
      )}
    </DashShell>
  );
};

export default ViewUser;
